import { useState } from "react";
import type { ReviewItem } from "../../shared/review-templates";
import type { ApprovalDTO, ReviewVersionDTO } from "../../shared/types";
import { ListRow } from "../components/List";
import { Dialog } from "../components/Dialog";
import { Button, ErrorText, inputCls, shortDate } from "../components/ui";
import { useTranscription, VoiceNote } from "../components/VoiceNote";
import { releaseState, verdictWord } from "../lib/approval-text";
import { part, text } from "./ReviewOutreachMessage";

// The outreach review page's controls: an item's other versions, the sheet the words of a verdict are written in, the
// bar under the thumb after an approval that did not settle, and the end of the queue.

/** Every version of this item, so a person can read what was filed before and take one of them after all. */
export function VersionHistory({ versions, showing, onShow, onClose }: {
  versions: ReviewVersionDTO[];
  showing: string | null;
  onShow: (id: string | null) => void;
  onClose: () => void;
}) {
  const latest = versions.at(-1);
  return (
    <Dialog open onClose={onClose} title="Versions of this message">
      {/* The kit's rows, without its card: a List here would be a box inside the popup's own box. */}
      <div className="-m-5">
        {versions.map((v) => {
          const reading = v.id === (showing ?? latest?.id);
          return (
            <ListRow
              key={v.id}
              className="border-b border-line last:border-0"
              onClick={() => {
                onShow(v.id === latest?.id ? null : v.id);
                onClose();
              }}
              leading={<span className="text-sm font-medium tabular-nums">v{v.version}</span>}
              title={<span className="text-sm">{verdictWord(v)}</span>}
              subtitle={
                <>
                  filed {shortDate(v.filedAt)}
                  {v.decidedByName ? ` · ${v.decidedByName}` : ""}
                  {v.decisionNote ? ` · \u201c${v.decisionNote}\u201d` : ""}
                </>
              }
              trailing={reading ? <span className="text-xs text-faint">reading</span> : undefined}
            />
          );
        })}
      </div>
    </Dialog>
  );
}

/** What each kind of words is for: the sheet's title, its button, and what the box asks. */
const SHEET = {
  revise: { title: "Request revision", send: "Send it back", placeholder: "What to change — the agent writes the next version from this" },
  reject: { title: "Reject this message", send: "Reject", placeholder: "Why not? Optional, but it stops the agent writing the same thing again" },
  withdraw: { title: "Withdraw this message", send: "Withdraw it", placeholder: "Why, for the agent — optional. It can never be sent after this" },
} as const;

/** Where a verdict's words are written — or why an approved message that did not go is withdrawn: a box, and the mic when the platform can transcribe. */
export function NoteSheet({ mode, about, onClose, onSend }: {
  mode: keyof typeof SHEET;
  /** The message the words are about, so a person typing still sees what they are sending back. */
  about: ApprovalDTO;
  onClose: () => void;
  onSend: (note: string) => Promise<void>;
}) {
  const transcription = useTranscription();
  const [note, setNote] = useState("");
  const [recording, setRecording] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const revise = mode === "revise";

  const send = async () => {
    if (revise && !note.trim()) return setError("Say what to change: a revision needs a note.");
    setBusy(true);
    setError(null);
    try {
      await onSend(note.trim());
    } catch (err) {
      setError((err as Error).message);
      setBusy(false);
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title={SHEET[mode].title}
      busy={busy}
      primary={
        <Button variant={revise ? "primary" : "danger"} onClick={send} disabled={busy || recording}>
          {SHEET[mode].send}
        </Button>
      }
    >
      <p className="mb-3 text-sm text-muted">
        <span className="font-medium text-fg">{text((about.payload ?? {}) as ReviewItem, "subject")}</span>
        {" · "}
        {part((about.payload ?? {}) as ReviewItem, "contact", "name")} ({part((about.payload ?? {}) as ReviewItem, "account", "name")})
      </p>
      <textarea
        className={inputCls}
        rows={5}
        autoFocus
        placeholder={SHEET[mode].placeholder}
        value={note}
        disabled={busy}
        onChange={(e) => setNote(e.target.value)}
      />
      <div className="mt-3">
        {transcription?.available ? (
          <VoiceNote
            maxSeconds={transcription.maxSeconds}
            disabled={busy}
            onBusy={setRecording}
            onText={(spoken) => setNote((current) => (current.trim() ? `${current.trim()} ${spoken}` : spoken))}
          />
        ) : transcription ? (
          <p className="text-sm text-muted">Speaking it is off on this platform — type it here.</p>
        ) : null}
      </div>
      <ErrorText error={error} />
    </Dialog>
  );
}

/** The thumb's buttons: one row, each a thumb's reach, the same height whatever the bar holds. */
export const thumbCls = "h-12 flex-1 text-reading";

/**
 * Under the thumb after an approval that did not settle: a message the tool refused stays on screen with why, and
 * Send again, Withdraw or Later for its addressee; one the tool may have sent says to check Outreach, offers "Checked
 * in Outreach" to whoever may say they looked (its addressee, an owner), and moves on.
 */
export function ReleaseBar({ a, canAct, canCheck, busy, onSend, onWithdraw, onChecked, onNext }: {
  a: ApprovalDTO;
  canAct: boolean;
  canCheck: boolean;
  onChecked: () => void;
  busy: boolean;
  onSend: () => void;
  onWithdraw: () => void;
  onNext: () => void;
}) {
  const uncertain = releaseState(a) === "uncertain";
  return (
    <>
      <p className="mb-2 text-sm break-words text-warning">
        {uncertain
          ? a.releaseCheckedAt
            ? `May have been sent — checked in Outreach by ${a.releaseCheckedByName ?? "someone"}.`
            : "May have been sent — check Outreach before anything else."
          : `Approved · not sent${a.releaseError ? `: ${a.releaseError}` : "."}`}
      </p>
      <div className="flex gap-2">
        {uncertain && !a.releaseCheckedAt && canCheck && (
          <Button className={thumbCls} disabled={busy} onClick={onChecked}>
            Checked in Outreach
          </Button>
        )}
        {!uncertain && canAct && (
          <>
            <Button variant="primary" className={thumbCls} disabled={busy} onClick={onSend}>
              Send again
            </Button>
            <Button className={thumbCls} disabled={busy} onClick={onWithdraw}>
              Withdraw
            </Button>
          </>
        )}
        <Button variant={uncertain || !canAct ? "primary" : "ghost"} className={thumbCls} disabled={busy} onClick={onNext}>
          {uncertain || !canAct ? "Next" : "Later"}
        </Button>
      </div>
    </>
  );
}

/** Nothing left in the queue: what was decided, and whatever has arrived since. */
export function AllDone({ count, more, onClose, onContinue }: { count: number; more: number; onClose: () => void; onContinue: () => void }) {
  return (
    <div className="card animate-card-in flex flex-col items-center gap-2 px-6 py-14 text-center">
      <span className="grid size-12 place-items-center rounded-full bg-success/10 text-xl text-success">✓</span>
      <span className="mt-1 text-heading font-semibold tracking-tight">All done</span>
      <span className="max-w-sm text-sm text-muted">
        {count === 0 ? "No outreach message is waiting for your verdict." : `Every one of the ${count} message${count === 1 ? "" : "s"} waiting for you has a verdict. The agents have been told.`}
      </span>
      <div className="mt-3 flex flex-wrap items-center justify-center gap-2">
        {more > 0 && (
          <Button variant="primary" onClick={onContinue}>
            {more} more arrived · review {more === 1 ? "it" : "them"}
          </Button>
        )}
        {more === 0 && (
          <Button onClick={onContinue}>Check again</Button>
        )}
        <Button variant="ghost" onClick={onClose}>
          Close
        </Button>
      </div>
    </div>
  );
}
